import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Bug, Paperclip, X, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  useCreatePlatformRequest,
  useInterpretPlatformRequest,
  useAnswerPlatformQuestion,
} from '../../hooks/backend/platform'
import { usePlatformUpload, MAX_UPLOAD_BYTES } from '../../hooks/usePlatformUpload'
import { writeErrorMessage } from '../../utils/writeError'
import { fileSize } from '../../utils/platform'

type Question = {
  id: string
  seq: number
  question: string
  answer: string | null
}

type ParsedQuestion = {
  prompt: string
  kind: 'choice' | 'text'
  options: string[]
}

/**
 * platform_request_question.question is still plain text in the database;
 * what changed is what the model writes into it. A question is now a JSON
 * object, {"prompt": "...", "kind": "choice"|"text", "options"?: [...]}, so a
 * multiple-choice question can carry its options through
 * platform_request_ask_v1 unchanged (it validates nothing beyond
 * non-blank). A row that does not parse, including every question asked
 * before this shipped, is shown as free text, which is what it always was.
 */
function parseQuestion(raw: string): ParsedQuestion {
  try {
    const parsed = JSON.parse(raw) as { prompt?: unknown; kind?: unknown; options?: unknown }
    if (parsed && typeof parsed.prompt === 'string' && parsed.prompt.trim()) {
      const options = Array.isArray(parsed.options)
        ? parsed.options.filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
        : []
      const kind = parsed.kind === 'choice' && options.length >= 2 ? 'choice' : 'text'
      return { prompt: parsed.prompt, kind, options }
    }
  } catch {
    // Not JSON. An older plain-text question, or a reply the model did not
    // shape correctly. Either way, free text is always a safe fallback.
  }
  return { prompt: raw, kind: 'text', options: [] }
}

/**
 * Report a problem, or ask for something, from wherever you happen to be
 * standing.
 *
 * ONE FIELD AND NOTHING ELSE, and that is the whole design decision in this
 * file. It does not ask for a title, an area, a severity or a priority. The
 * person filling this in is a Driver Servant in the middle of something, and
 * every field added here is a reason to send a WhatsApp message instead, after
 * which the problem has no record, no date and nobody it belongs to. What they
 * owe is a sentence. Reading it is the model's job and deciding about it is
 * Luyanda's or Leduma's.
 *
 * WHAT THEY ARE NOT TOLD, DELIBERATELY: a date. The dialog closes saying the
 * report is filed and that they will get a date once somebody has read it. It
 * does not print the provisional band, because a number shown at the moment of
 * reporting is heard as a promise, and no person has looked at it yet.
 *
 * THE REPORT IS SAVED BEFORE ANYTHING ELSE HAPPENS. Screenshots, the model and
 * the questions all come after. If the model is down, the network drops or the
 * person closes the laptop at question two, the report is on the board and the
 * conversation can be finished later from the Platform Requests screen. Holding
 * the report back until the exchange completes would lose the whole thing to one
 * bad moment, which is the same trade DesignRequestDialog describes.
 */
export function ReportProblemDialog({
  open,
  onOpenChange,
  onCreated,
  prefill,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: () => void
  // Text the dialog opens with. The Manual page passes the section reference, so
  // a report about a wrong section names the section without anyone typing it.
  prefill?: string
}) {
  const { pathname } = useLocation()
  const create = useCreatePlatformRequest()
  const interpret = useInterpretPlatformRequest()
  const answer = useAnswerPlatformQuestion()
  const { upload } = usePlatformUpload()

  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [requestId, setRequestId] = useState<string | null>(null)
  const [requestRef, setRequestRef] = useState<number | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [draft, setDraft] = useState('')
  const [phase, setPhase] = useState<'writing' | 'asking' | 'done'>('writing')
  const [readingFailed, setReadingFailed] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const createTrigger = create.trigger
  const interpretTrigger = interpret.trigger
  const answerTrigger = answer.trigger

  // Reset on every open, not on close. A dialog that keeps last time's text is a
  // dialog that files last time's text.
  useEffect(() => {
    if (!open) return
    setText(prefill ?? '')
    setFiles([])
    setBusy(false)
    setRequestId(null)
    setRequestRef(null)
    setQuestions([])
    setDraft('')
    setPhase('writing')
    setReadingFailed(null)
  }, [open, prefill])

  const openQuestion = questions.find((q) => q.answer === null) ?? null

  const runInterpret = useCallback(
    async (id: string) => {
      try {
        const res = (await interpretTrigger({ id }, { skipCache: true })) as
          | { outcome?: string; questions?: Question[] }
          | null
        if (res?.outcome === 'questions' && Array.isArray(res.questions)) {
          setQuestions(res.questions)
          setPhase('asking')
          return
        }
        setPhase('done')
      } catch (err) {
        // A model failure is not a lost report, and the sentence says so rather
        // than looking like the report failed.
        setReadingFailed(writeErrorMessage(err))
        setPhase('done')
      }
    },
    [interpretTrigger],
  )

  async function submitReport() {
    if (!text.trim()) {
      toast.error('Say what happened, in your own words. One sentence is enough.')
      return
    }
    setBusy(true)
    try {
      const row = (await createTrigger(
        { reported_text: text.trim(), page_path: pathname },
        { skipCache: true },
      )) as { id?: string; ref?: number } | null
      const id = row?.id ?? null
      if (!id) throw new Error('The report did not come back with a reference.')
      setRequestId(id)
      setRequestRef(row?.ref ?? null)
      onCreated?.()

      if (files.length > 0) {
        const outcome = await upload(files, id)
        for (const line of outcome.failed) toast.error(line)
        for (const name of outcome.tooBig) {
          toast.error(`${name} is over ${fileSize(MAX_UPLOAD_BYTES)} and was not attached.`)
        }
        if (outcome.done > 0 && outcome.done < files.length) {
          toast.warning(`${outcome.done} of ${files.length} screenshots attached.`)
        }
      }

      await runInterpret(id)
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function submitAnswer(explicitValue?: string) {
    if (!requestId || !openQuestion) return
    const value = (explicitValue ?? draft).trim()
    if (!value) {
      toast.error('Type an answer, or choose one.')
      return
    }
    setBusy(true)
    try {
      const res = (await answerTrigger(
        { id: requestId, seq: openQuestion.seq, answer: value },
        { skipCache: true },
      )) as { questions?: Question[] } | null
      if (Array.isArray(res?.questions)) setQuestions(res.questions)
      setDraft('')
      // The model decides whether it needs another question. Asking it after
      // every answer rather than requesting five up front is what lets it stop
      // early: three of the five questions are usually unnecessary once the
      // screenshot has been read.
      await runInterpret(requestId)
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-4 w-4 text-[hsl(var(--brand))]" />
            {phase === 'writing'
              ? 'Report a problem, or ask for something'
              : phase === 'asking'
                ? `Report ${requestRef ? '#' + requestRef : ''}, a couple of questions`
                : `Report ${requestRef ? '#' + requestRef : ''} is filed`}
          </DialogTitle>
          <DialogDescription>
            {phase === 'writing'
              ? 'Describe it the way you would tell a colleague. A screenshot helps more than anything you could type.'
              : phase === 'asking'
                ? 'Each answer makes the fix quicker. You can say I do not know, and you can close this and finish later.'
                : 'It is on the platform board. Nothing else is needed from you.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'writing' && (
          <div className="space-y-4">
            <Textarea
              autoFocus
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What did you expect to happen, and what happened instead?"
            />
            <p className="text-[11px] text-muted-foreground">
              The screen you are on ({pathname}) is recorded automatically, so you do not need to
              say where you were.
            </p>

            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])])
                  if (fileRef.current) fileRef.current.value = ''
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                <Paperclip className="mr-2 h-3.5 w-3.5" /> Add a screenshot
              </Button>
              {files.length > 0 && (
                <ul className="space-y-1">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs"
                    >
                      <span className="truncate">
                        {f.name} <span className="text-muted-foreground">{fileSize(f.size)}</span>
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${f.name}`}
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {phase === 'asking' && openQuestion && (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-card px-3 py-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Question {openQuestion.seq} of at most 5
              </p>
              <p className="mt-1 text-sm font-medium">{parseQuestion(openQuestion.question).prompt}</p>
            </div>
            {parseQuestion(openQuestion.question).kind === 'choice' ? (
              <div className="flex flex-wrap gap-2">
                {parseQuestion(openQuestion.question).options.map((opt) => (
                  <Button
                    key={opt}
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => void submitAnswer(opt)}
                  >
                    {opt}
                  </Button>
                ))}
              </div>
            ) : (
              <Textarea
                autoFocus
                rows={3}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Your answer"
              />
            )}
            {questions.filter((q) => q.answer !== null).length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">
                  {questions.filter((q) => q.answer !== null).length} already answered
                </summary>
                <ul className="mt-2 space-y-2">
                  {questions
                    .filter((q) => q.answer !== null)
                    .map((q) => (
                      <li key={q.id}>
                        <span className="block font-medium text-foreground">
                          {parseQuestion(q.question).prompt}
                        </span>
                        <span>{q.answer}</span>
                      </li>
                    ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-3 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
              <div>
                <p className="font-medium">
                  Filed as report {requestRef ? `#${requestRef}` : ''}.
                </p>
                {/* NO DATE HERE, ON PURPOSE. A number shown now is heard as a
                    promise, and nobody has read this yet. */}
                <p className="mt-1 text-muted-foreground">
                  You will get a date for it once it has been read and sized. It is on the
                  Platform Requests screen if you want to add anything.
                </p>
              </div>
            </div>
            {readingFailed && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {readingFailed}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'writing' && (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void submitReport()} disabled={busy}>
                {busy ? <Spinner className="mr-2 h-3.5 w-3.5" /> : null}
                File it
              </Button>
            </>
          )}
          {phase === 'asking' && (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Finish later
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void submitAnswer('I do not know')}
                disabled={busy}
              >
                I do not know
              </Button>
              {openQuestion && parseQuestion(openQuestion.question).kind !== 'choice' && (
                <Button type="button" onClick={() => void submitAnswer()} disabled={busy}>
                  {busy ? <Spinner className="mr-2 h-3.5 w-3.5" /> : null}
                  Answer
                </Button>
              )}
            </>
          )}
          {phase === 'done' && (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
